import type Map from 'ol/Map';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Geometry } from 'ol/geom';
import type { SimpleGeometry } from 'ol/geom';
import type { Coordinate } from 'ol/coordinate';

import { FEATURE_TYPE } from '@/components/map/consts/mapFeatureConsts';
import type { MapFeatureOverlayItem } from '@/stores/types/mapStoreContracts';
import {
	asTypedMapFeature,
	isTypedMapFeatureOfType,
} from '@/components/map/utils/mapFeatureGuards';


type XYasArray = readonly [number, number];


const toXYasArray = (coord: Coordinate): XYasArray => {
	const [x, y] = coord;
	if (x === undefined || y === undefined) {
		throw new Error('Invalid coordinate length: ' + String(coord.length));
	}
	return [x, y];
};


const isSimpleGeometry = (geometry: Geometry): geometry is SimpleGeometry => (
	'getCoordinates' in geometry && typeof geometry.getCoordinates === 'function'
);


const getPointCoordinate = (geometry: Geometry): Coordinate | null => {
	if (!isSimpleGeometry(geometry)) {
		return null;
	}
	const rawCoords = geometry.getCoordinates();
	if (!Array.isArray(rawCoords) || rawCoords.length < 2) {
		return null;
	}
	const x = rawCoords[0];
	const y = rawCoords[1];
	if (typeof x !== 'number' || typeof y !== 'number') {
		return null;
	}
	return [x, y];
};


const getGnssFeatures = (
	mapInstance: Map | null,
	vectorLayer: VectorLayer<VectorSource> | null,
): MapFeatureOverlayItem[] => {
	if (mapInstance == null || vectorLayer == null) {
		return [];
	}
	const source = vectorLayer.getSource();
	if (source == null) {
		return [];
	}
	const result: MapFeatureOverlayItem[] = [];
	source.forEachFeature(feature => {
		const typed = asTypedMapFeature(feature);
		if (
			typed == null
			|| (
				!isTypedMapFeatureOfType(typed, FEATURE_TYPE.baseStation)
				&& !isTypedMapFeatureOfType(typed, FEATURE_TYPE.gridpoint)
			)
		) {
			return;
		}
		const geometry = feature.getGeometry();
		if (geometry == null) {
			return;
		}
		const coordinate = getPointCoordinate(geometry);
		if (coordinate == null) {
			return;
		}
		const pixel = toXYasArray(mapInstance.getPixelFromCoordinate(coordinate));
		result.push({
			featureType: typed.type,
			featureData: typed.featureData,
			coords: {
				left: String(pixel[0]) + 'px',
				top: String(pixel[1]) + 'px',
			},
		});
	});
	return result;
};


export {
	getGnssFeatures,
};
